export type CompoundingFrequency =
  | "annually"
  | "semiannually"
  | "quarterly"
  | "monthly";

export type YearlyBreakdown = {
  year: number;
  startBalance: number;
  interestEarned: number;
  endBalance: number;
};

export type CompoundInterestResult = {
  finalAmount: number;
  totalInterest: number;
  breakdown: YearlyBreakdown[];
};

const PERIODS_PER_YEAR: Record<CompoundingFrequency, number> = {
  annually: 1,
  semiannually: 2,
  quarterly: 4,
  monthly: 12,
};

/**
 * Computes compound growth on principal only (no contributions).
 * The yearly breakdown groups periods into years.
 */
export function calculateCompoundInterest(
  principal: number,
  annualRatePercent: number,
  years: number,
  frequency: CompoundingFrequency,
): CompoundInterestResult {
  const periodsPerYear = PERIODS_PER_YEAR[frequency] ?? 1;
  const ratePerPeriod = annualRatePercent / 100 / periodsPerYear;

  const breakdown: YearlyBreakdown[] = [];
  let currentBalance = principal;

  for (let year = 1; year <= years; year++) {
    const startBalance = currentBalance;

    for (let period = 0; period < periodsPerYear; period++) {
      currentBalance = currentBalance * (1 + ratePerPeriod);
    }

    const interestEarned = currentBalance - startBalance;
    breakdown.push({ year, startBalance, interestEarned, endBalance: currentBalance });
  }

  return {
    finalAmount: currentBalance,
    totalInterest: currentBalance - principal,
    breakdown,
  };
}

export function periodsPerYear(frequency: CompoundingFrequency): number {
  return PERIODS_PER_YEAR[frequency] ?? 1;
}

